import AlertMessage from '@/components/AlertMessage';
import Banner from '@/components/Banner';
import {
  CheckboxField,
  DatePickerField,
  InputField,
  TextareaField,
} from '@/components/FormElements';
import PaymentMethod from '@/components/PaymentMethod';
import ShareDialog from '@/components/ShareDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { groupBy } from '@/lib/utils';
import { Course, CoursePricingOptions, PageProps } from '@/types';
import { yupResolver } from '@hookform/resolvers/yup';
import { router, usePage } from '@inertiajs/react';
import { format, subYears } from 'date-fns';
import { Check } from 'lucide-react';
import { FormEventHandler, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import * as yup from 'yup';

import SubscriptionDialog, { PaymentSchema } from './SubscriptionDialog';

type Fields = keyof CoursePricingOptions;

// interface PriceDetails {
//   id: number;
//   duration: string;
//   certificate: string;
//   price: number;
//   actual_price: number;
// }

const courseFieldLabels: Record<Fields, string> = {
  age_group: 'Age Group',
  language_selection: 'Language Selection',
  level: 'Level',
  learning_mode: 'Learning Mode',
  batch: 'Batch',
};

const ApplicationSchema = yup
  .object({
    student_name: yup.string().trim().required('Student Name is required'),
    address: yup.string().required('Address is required'),
    dob: yup.date().required('Date of Birth is required'),
    other_info: yup.string().defined(),
    amount: yup.number().positive('Amount must be positive').defined(),
  })
  .concat(PaymentSchema);

type ApplicationFormData = yup.InferType<typeof ApplicationSchema> & {
  user_id?: number;
  course_pricing_option_id?: number;
};

const courseOptions = [
  'Handwriting Tamil',
  'Handwriting English',
  'Handwriting Hindi',
  'Speedwriting Tamil',
  'Speedwriting English',
  'Speedwriting Hindi',
  'Calligraphy English',
  'Spoken Hindi',
  'Spoken English',
  'Spoken Tamil',
  'Abacus one level',
  'English Reading and Writing',
  'Tamil Reading and Writing',
  'Hindi Reading and Writing',
  'Bharathanatyam',
  'Western Dance',
  'Vocal',
  'Drawing and Painting',
  'Keyboard',
  'Drums',
  'Zumba',
];

const initialOptionData = {
  age_group: '',
  language_selection: '',
  level: '',
  learning_mode: '',
  batch: '',
};

const ShowCourse = ({ course }: { course: Course }) => {
  const { user } = usePage<PageProps>().props.auth;
  const [fields, setFields] = useState<Fields[]>([]);
  const [groupedOptions, setGroupedOptions] = useState<
    ReturnType<typeof groupBy>
  >({});
  const [nextField, setNextField] = useState(0);
  const [options, setOptions] = useState<Record<Fields, string[]>>({
    age_group: [],
    language_selection: [],
    level: [],
    learning_mode: [],
    batch: [],
  });

  const [summerCampCourses, setSummerCampCourses] = useState(courseOptions);

  const [optionData, setOptionData] =
    useState<Record<Fields, string>>(initialOptionData);

  // const [priceDetail, setPriceDetail] = useState<PriceDetails>();
  const [openDialog, setOpenDialog] = useState(false);
  const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [result, setResult] = useState<{
    status: 'pending' | 'success' | 'error';
    message: string;
  }>();

  const toDate = subYears(new Date(), 1);

  const form = useForm<ApplicationFormData>({
    resolver: yupResolver(ApplicationSchema),
    defaultValues: {
      user_id: user?.id ?? '',
      student_name: '',
      address: '',
      dob: undefined,
      amount: 0,
      no_refund_consent: false,
      transaction_no: '',
      course_pricing_option_id: -1,
      other_info: window.location.pathname.endsWith('summer-camp') ? '[]' : '',
    },
  });

  // destructure form state to subscribe to proxy form state
  const { isSubmitting } = form.formState;

  useEffect(() => {
    window.dataLayer?.push({
      event: 'view_item',
      ecommerce: {
        items: [
          {
            item_id: String(course.id),
            item_name: course.title,
          },
        ],
      },
    });
    // Fire once per page load, not on every re-render this component goes through.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course.id]);

  const resetState = () => {
    setOptionData(initialOptionData);
    setNextField(0);
    setOptions({
      age_group: [...options.age_group],
      language_selection: [],
      level: [],
      learning_mode: [],
      batch: [],
    });
    // setPriceDetail(undefined);
    setSummerCampCourses(courseOptions);
    form.reset();
  };

  useEffect(() => {
    // Check if pricing options are available
    if (course.pricing_options.length > 0) {
      // Extract required fields that have values in the first pricing option
      const requiredFields: Fields[] = (
        Object.keys(courseFieldLabels) as Fields[]
      ).filter(
        (field) =>
          course.pricing_options[0][field] &&
          course.pricing_options[0][field] !== ''
      );

      // Group dependent options based on required fields
      const groupedDependentOptions = groupBy(
        course.pricing_options,
        requiredFields
      );

      // Update state variables with the extracted data
      setFields(requiredFields);
      setOptions((prevOptions) => ({
        ...prevOptions,
        [requiredFields[0]]: Object.keys(groupedDependentOptions),
      }));
      setGroupedOptions(groupedDependentOptions);
    }
  }, [course.pricing_options]);

  const getDependentOptions = (
    fieldData: typeof optionData,
    depth: number
  ): Record<string | number, unknown> => {
    let currentDepth: Record<string | number, unknown> = groupedOptions;

    // Iterate through the fields up to the specified depth
    for (let i = 0; i < depth; i++) {
      const fieldValue = fieldData[fields[i]]; // Get the value of the current field

      if (typeof currentDepth === 'object' && currentDepth !== null) {
        currentDepth =
          (currentDepth[fieldValue] as Record<string | number, unknown>) ?? {};
      } else {
        return {};
      }
    }
    return currentDepth;
  };

  // const getPrice = async (priceId: number) => {
  //   const cacheKey = `price-${priceId}`;

  //   // Retrieve and parse price details from session storage, if available
  //   const getStoredPriceDetail = () => {
  //     const storedData = sessionStorage.getItem(cacheKey);
  //     return storedData ? (JSON.parse(storedData) as PriceDetails) : null;
  //   };

  //   // Save price detail to session storage
  //   const storePriceDetail = (data: unknown) => {
  //     sessionStorage.setItem(cacheKey, JSON.stringify(data));
  //   };

  //   // Fetch price detail from API and store in session storage
  //   const fetchPriceDetail = async () => {
  //     try {
  //       const response = await axios.post(`/api/courses/pricing/${priceId}`);
  //       storePriceDetail(response.data as PriceDetails);
  //       return response.data as PriceDetails;
  //     } catch (error) {
  //       toast.error('Error fetching price', {
  //         description: (error as AxiosError).message,
  //         closeButton: true,
  //       });
  //       throw error;
  //     }
  //   };

  //   // Retrieve stored data or fetch if not available
  //   const priceDetail = getStoredPriceDetail() ?? (await fetchPriceDetail());
  //   return priceDetail;
  // };

  const handleOptionChange = (
    field: keyof CoursePricingOptions,
    option: string,
    index: number
  ) => {
    // Create copies of data and options
    const updatedData = { ...optionData };
    const updatedOptions = { ...options };

    // if (nextField === fields.length && index < nextField) {
    //   setPriceDetail(undefined);
    // }

    // Clear and update data and options
    if (index < nextField - 1) {
      fields.slice(index + 1, nextField).forEach((field) => {
        updatedData[field] = '';
        updatedOptions[field] = [];
      });
    }
    updatedData[field] = option;
    updatedOptions[fields[index + 1]] = Object.keys(
      getDependentOptions(updatedData, index + 1)
    );

    // Update state and nextField
    setOptionData(updatedData);
    setOptions(updatedOptions);
    setNextField(index + 1);

    // Fetch price if all fields are selected
    if (index + 1 === fields.length) {
      const priceId = getDependentOptions(updatedData, index + 1).id as number;
      form.setValue('course_pricing_option_id', priceId);
      //   const priceDetail = await getPrice(priceId);
      //   setPriceDetail(priceDetail);
    }
  };

  const handleSummerCampSelection = (
    value: string[],
    onChange: (value: string) => void
  ) => {
    if (value.length <= parseInt(optionData.level.slice(-1))) {
      onChange(JSON.stringify(value));
    }
  };

  const onEnroll: FormEventHandler = (e) => {
    e.preventDefault();

    if (!user) {
      router.visit(
        route('login', {
          redirect: window.location.pathname + window.location.search,
        })
      );
      return;
    }

    if (
      window.location.pathname.endsWith('summer-camp') &&
      (JSON.parse(form.getValues('other_info')) as string[]).length !==
        parseInt(optionData.level.slice(-1))
    ) {
      toast.error(`Please select ${optionData.level} courses`);
      return;
    }

    if (!user.subscription_status) {
      setOpenSubscriptionDialog(true);
    } else if (user.subscription_status === 'Pending') {
      setResult({
        status: 'pending',
        message:
          'Your One-time Registration is being processed. Please Come back later or contact us for further details. You might get a call from our team.',
      });
      setOpenAlert(true);
    } else {
      // if (priceDetail) {
      //   form.setValue('course_pricing_option_id', priceDetail.id);
      //   form.setValue('amount', priceDetail.price);
      window.dataLayer?.push({
        event: 'begin_checkout',
        ecommerce: {
          items: [
            {
              item_id: String(course.id),
              item_name: course.title,
            },
          ],
        },
      });
      setOpenDialog(true);
    }
  };

  const onValidData = async (data: ApplicationFormData) => {
    await new Promise<void>((resolve) =>
      router.post(
        route('courses.application'),
        { ...data, dob: format(data.dob, 'yyyy-MM-dd') },
        {
          onError: (errors) => {
            setResult({
              status: 'error',
              message: errors[0] ?? 'Something went wrong. Retry!',
            });
          },
          onSuccess: ({ props }) => {
            if (props.status) {
              setResult({ status: 'success', message: props.status as string });
              resetState();
            }
          },
          onFinish: () => {
            setOpenDialog(false);
            setOpenAlert(true);
            resolve();
          },
        }
      )
    );
  };

  return (
    <>
      <AlertMessage open={openAlert} onClose={setOpenAlert} {...result} />
      <SubscriptionDialog
        user_id={user?.id}
        open={openSubscriptionDialog}
        onClose={setOpenSubscriptionDialog}
      />
      <Drawer
        open={openDialog}
        onOpenChange={(state) => {
          form.clearErrors();
          setOpenDialog(state);
        }}
      >
        <DrawerContent className="sm:max-w-sm max-h-screen mx-auto">
          <DrawerHeader>
            <DrawerTitle>Payment Details</DrawerTitle>
            <DrawerDescription>
              Fill the below form to apply for this course.
            </DrawerDescription>
          </DrawerHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onValidData)}
              className="flex flex-col gap-10 p-4 overflow-auto"
            >
              <div className="flex flex-col gap-4">
                <Typography.Subtle className="font-semibold">
                  Scan and Pay
                </Typography.Subtle>
                <InputField
                  control={form.control}
                  name="amount"
                  label="Amount to be Paid"
                  startDecorator={
                    <span className="px-4 text-primary text-lg">₹</span>
                  }
                />
                <Typography.Subtle className="w-fit font-semibold px-1 bg-primary-muted text-primary text-base">
                  Non-Refundable
                </Typography.Subtle>
                <PaymentMethod />
              </div>
              <div className="grid w-full max-w-sm items-center gap-6">
                <InputField
                  control={form.control}
                  name="student_name"
                  label="Student Name"
                />

                <DatePickerField
                  control={form.control}
                  name="dob"
                  label="Date of Birth"
                  fromDate={subYears(toDate, 80)}
                  toDate={toDate}
                />

                <TextareaField
                  control={form.control}
                  name="address"
                  label="Address"
                  autoComplete="street-address"
                />

                <CheckboxField
                  control={form.control}
                  name="no_refund_consent"
                  label="No Refund Policy"
                  description={() =>
                    'By proceeding, you acknowledge and agree that no refunds will be issued for this purchase or transaction. All sales are final.'
                  }
                />

                <InputField
                  control={form.control}
                  name="transaction_no"
                  label="Transaction No."
                />
              </div>
              <Button type="submit" disabled={isSubmitting}>
                Submit
              </Button>
            </form>
          </Form>
        </DrawerContent>
      </Drawer>

      <Banner
        image={course.image_url}
        alt={course.title}
        tagline={course.category.title}
        title={course.title}
        description={course.description}
      >
        <div className="flex flex-col items-start gap-4">
          {course.duration && (
            <div className="flex flex-col gap-1">
              <Typography.Small className="text-ink-foreground/60">
                Duration
              </Typography.Small>
              <Typography.Body className="font-semibold">
                {course.duration}
              </Typography.Body>
            </div>
          )}
          <ShareDialog
            url={window.location.href}
            title={course.title + ' - ' + import.meta.env.VITE_APP_NAME}
            content={course.description}
          />
        </div>
      </Banner>

      {/* Visually hidden: the Banner above already shows the course
          description as body text under its h1, but that's a paragraph, not
          a heading, so screen readers and heading-order checks see a raw
          h1 -> h3 jump (Customize Your Course) without a real h2 to land on. */}
      <Typography.H2 className="sr-only">About {course.title}</Typography.H2>

      <Band tone="surface">
        <SectionLayout space="lg">
          <div className="grid auto-rows-auto grid-cols-1 gap-8 md:grid-cols-2">
            <div className="flex h-fit w-full max-w-lg flex-col gap-y-5 rounded-lg border bg-card p-6 shadow-pop md:col-start-2 md:row-span-2">
              <Typography.H3>Customize Your Course</Typography.H3>

              <Form {...form}>
                <form
                  onSubmit={onEnroll}
                  className="flex flex-col gap-6 space-y-4"
                >
                  <div className="flex flex-col gap-6">
                    {fields.map((field, index) => {
                      let label = courseFieldLabels[field];
                      label =
                        course.title === 'Summer Camp' && label === 'Level'
                          ? 'No. of Courses'
                          : label;
                      const fieldId = `course-field-${field}`;
                      return (
                        <div key={field} className="space-y-1">
                          <Label htmlFor={fieldId}>{label}</Label>
                          <Select
                            name={field}
                            onValueChange={(option) =>
                              handleOptionChange(field, option, index)
                            }
                            value={optionData[field]}
                            disabled={options[field].length === 0}
                          >
                            <SelectTrigger id={fieldId}>
                              <SelectValue placeholder={`Select ${label}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {options[field].map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      );
                    })}
                  </div>

                  {/* {course.pricing_options.length > 0 && (
                    <div className="bg-primary-muted px-2 py-1 space-y-2">
                      {priceDetail ? (
                        <>
                          <div className="inline-flex items-center gap-4">
                            <Typography.Body className="font-semibold">
                              &#8377; {priceDetail.price}
                            </Typography.Body>
                            <Typography.Subtle className="line-through">
                              &#8377; {priceDetail.actual_price}
                            </Typography.Subtle>
                            <Typography.Body className="text-primary">
                              {calculatePercentOff(
                                priceDetail.actual_price,
                                priceDetail.price
                              )}{' '}
                              off
                            </Typography.Body>
                          </div>
                          <div className="flex flex-wrap items-center gap-4">
                            <Typography.Subtle>
                              Duration: <strong>{priceDetail.duration}</strong>
                            </Typography.Subtle>
                            <Typography.Subtle>
                              Certificate:{' '}
                              <strong>
                                {priceDetail.certificate
                                  ? 'Provided'
                                  : 'Not Provided'}
                              </strong>
                            </Typography.Subtle>
                          </div>
                        </>
                      ) : (
                        <Typography.Body>
                          Choose above options to see price
                        </Typography.Body>
                      )}
                    </div>
                  )} */}

                  {window.location.pathname.endsWith('summer-camp') ? (
                    optionData.level !== '' && (
                      <div className="flex flex-col gap-4">
                        <Typography.Large>
                          Choose {optionData.level} Courses
                        </Typography.Large>

                        <FormField
                          control={form.control}
                          name="other_info"
                          render={({ field: { value, onChange } }) => (
                            <FormItem>
                              <FormControl>
                                <ToggleGroup
                                  type="multiple"
                                  value={JSON.parse(value) as string[]}
                                  onValueChange={(value) =>
                                    handleSummerCampSelection(value, onChange)
                                  }
                                  className="flex flex-wrap justify-start gap-2"
                                  variant="outline"
                                >
                                  {summerCampCourses.map((option, index) => (
                                    <ToggleGroupItem
                                      key={index}
                                      value={option}
                                      className="h-auto text-primary data-[state=on]:bg-background data-[state=on]:border-primary focus:outline-hidden border-2 border-input bg-popover p-2"
                                    >
                                      {option}
                                    </ToggleGroupItem>
                                  ))}

                                  <Input
                                    placeholder='Type & Press "Enter" to add...'
                                    onKeyDown={(e) => {
                                      if (
                                        e.key === 'Enter' &&
                                        e.currentTarget.value !== ''
                                      ) {
                                        e.preventDefault();

                                        const newValue = e.currentTarget.value;
                                        const courses = new Set(
                                          summerCampCourses
                                        );
                                        if (!courses.has(newValue)) {
                                          setSummerCampCourses([
                                            ...summerCampCourses,
                                            newValue,
                                          ]);
                                          handleSummerCampSelection(
                                            [
                                              ...(JSON.parse(
                                                value
                                              ) as string[]),
                                              newValue,
                                            ],
                                            onChange
                                          );
                                        } else {
                                          toast.error(
                                            `Course ${newValue} already selected`
                                          );
                                        }
                                        e.currentTarget.value = '';
                                      }
                                    }}
                                  />
                                </ToggleGroup>
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    )
                  ) : (
                    <TextareaField
                      control={form.control}
                      name="other_info"
                      label="Other Info"
                      placeholder="Enter any other info"
                      rows={5}
                      description={(val) => `${val?.length} / 255 chars`}
                    />
                  )}

                  <div className="flex *:flex-1 gap-4">
                    <Button type="submit" disabled={nextField < fields.length}>
                      Enroll Now
                    </Button>
                    <ShareDialog
                      url={window.location.href}
                      title={course.title}
                      content={course.description}
                    />
                  </div>
                </form>
              </Form>
            </div>

            <div className="flex flex-col gap-y-10 md:row-start-1">
              {course.skills_gained && (
                <div className="flex flex-col gap-y-4">
                  <Typography.H3>Skills You&apos;ll Gain</Typography.H3>
                  <div className="flex flex-wrap gap-2">
                    {(JSON.parse(course.skills_gained) as string[]).map(
                      (skill, index) => (
                        <Badge
                          key={index}
                          variant="default"
                          className="py-1 text-sm font-medium"
                        >
                          {skill}
                        </Badge>
                      )
                    )}
                  </div>
                </div>
              )}

              {(course.youtube_url_ta ?? course.youtube_url_en) && (
                <div className="flex flex-col gap-y-4">
                  <iframe
                    className="aspect-video w-full rounded-lg"
                    loading="lazy"
                    src={course.youtube_url_ta ?? course.youtube_url_en!}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />

                  {course.youtube_url_ta && course.youtube_url_en && (
                    <Button asChild variant="outline">
                      <a
                        href={course.youtube_url_en}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-fit"
                      >
                        Watch in English
                      </a>
                    </Button>
                  )}
                </div>
              )}

              {course.highlights && (
                <div className="flex flex-col gap-y-4">
                  <Typography.H3>Highlights</Typography.H3>
                  <ul className="flex flex-col gap-y-4">
                    {(JSON.parse(course.highlights) as string[][]).map(
                      (highlight, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <Check
                            size={20}
                            className="mt-1 shrink-0 text-primary"
                          />
                          <div className="flex flex-col gap-1">
                            <Typography.Body className="font-medium">
                              {highlight[0]}
                            </Typography.Body>
                            {highlight[1] && (
                              <Typography.Subtle>
                                {highlight[1]}
                              </Typography.Subtle>
                            )}
                          </div>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </SectionLayout>
      </Band>
    </>
  );
};

export default ShowCourse;
